'use client'

import type { NavigationDesktopDropdownProps } from '@maestro/commerce-blocks/components/navigation-header/navigation-header.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import React from 'react'

import { useNavigationDropdown } from '@maestro/commerce-blocks/components/navigation-header/batons/use-navigation-desktop-dropdown'
import { theme } from '@maestro/local/theme'
import { Button } from '@pearlconvert/ui/components/button'
import { Link } from '@pearlconvert/ui/components/link'

export default function NavigationDesktopDropdown(
  props: NavigationDesktopDropdownProps
) {
  const {
    title,
    node,
    nodes,
    isMegaDropdown,
    isActive,
    closeDropdown,
    submenuId,
  } = props
  const t = useTranslations('commerce-blocks')
  const { openOnClick: isOpenOnClick } = theme().navigation

  const { handleClickMenu, handleKeyDown } = useNavigationDropdown({
    title,
    closeDropdown,
    isActive,
  })

  return (
    <div
      id={submenuId}
      className={classNames({
        'nav-desktop__dropdown--is-mega-dropdown': isMegaDropdown,
        'nav-desktop__dropdown': !isMegaDropdown,
        open: isActive,
        closed: !isActive,
      })}
      onKeyDown={handleKeyDown}
    >
      <div
        className={classNames('nav-desktop__dropdown-container', {
          container: isMegaDropdown,
        })}
      >
        <ul
          className={classNames({
            'nav-desktop__subnav--is-mega-dropdown': isMegaDropdown,
            'nav-desktop__subnav': !isMegaDropdown,
          })}
          aria-label={`${title} menu`}
          style={{ '--items': nodes?.length } as React.CSSProperties}
        >
          {isOpenOnClick && (
            <li className="nav-desktop__subitem">
              <Link
                className={classNames('nav-desktop__sublink', node.cssClass)}
                href={node.url}
                target={node.target}
                aria-label={t('navigation-header.aria-all-in', {
                  title: node.title,
                })}
                onClick={() => handleClickMenu()}
              >
                {t('navigation-header.all-in', { title: node.title })}
              </Link>
            </li>
          )}
          {nodes?.map((node) => (
            <li className="nav-desktop__subitem" key={node._key}>
              {isMegaDropdown ? (
                <h2 className="nav-desktop__subtitle">
                  <Link
                    className={classNames(
                      'nav-desktop__sublink',
                      node.cssClass
                    )}
                    href={node.url}
                    target={node.target}
                    onClick={() => handleClickMenu(node.title)}
                  >
                    {node.title}
                  </Link>
                </h2>
              ) : (
                <Link
                  className={classNames('nav-desktop__sublink', node.cssClass)}
                  href={node.url}
                  target={node.target}
                  onClick={() => handleClickMenu(node.title)}
                >
                  {node.title}
                </Link>
              )}
              {node.children && (
                <ul className="nav-desktop__subnav" aria-label={node.title}>
                  {node.children?.map((child) => (
                    <li
                      className="nav-desktop__subitem"
                      role="none"
                      key={child._key}
                    >
                      <Link
                        className={classNames(
                          'nav-desktop__sublink',
                          child.cssClass
                        )}
                        href={child.url}
                        target={child.target}
                        onClick={() =>
                          handleClickMenu(node.title || '', child.title || '')
                        }
                      >
                        {child.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        {isMegaDropdown && (
          <Button
            size="fixed"
            className="nav-desktop__close"
            onClick={closeDropdown}
            title={t('navigation-header.close-menu')}
            type="empty"
            iconLeft="close"
          />
        )}
      </div>
    </div>
  )
}
